import stats from './stats';

const ZMAIL_URL = process.env.REACT_APP_BACKEND_URL;

const MAIL_URL = `${ZMAIL_URL}/messages`;
const ATT_BASE_URL = `${ZMAIL_URL}/attachment`;

const headers = {
  'Content-Type': 'application/json',
  'Accept': '*/*',
  'Access-Control-Allow-Origin': '*',
};

const GET_REQ_OPTIONS = {
  method: 'GET',
  headers: headers,
  redirect: 'follow',
};

/**
 * fetchMessage
 * @param {string} messageId
 * @param {string} tok token
 * @param {string} dc datacenter identifier
 * @return {Promise} fetch message response
 */
export const fetchMessage = stats.withMetric(
    {
      metric: stats.METRIC.GET_MSG,
      description: 'fetchMsg',
    },
    async function fetchMessage({dc, messageId, tok}) {
      const queryParams = new URLSearchParams({
        id: messageId,
        tok,
        dc,
        format: 'encrypted',
      });
      const response = await fetch(
          MAIL_URL + '?' + queryParams.toString(),
          GET_REQ_OPTIONS,
      );
      if (response.redirected) {
        console.log('Redirected to', response.url);
      }
      if (!response.ok) {
        console.log('Failed to fetch email');
        throw new Error(
            `Email fetch failed: ${response.status} ${response.statusText}`,
        );
      }
      return response.json();
    },
);

const GET_ATT_OPTIONS = {
  method: 'GET',
  headers: headers,
  redirect: 'follow',
  responseType: 'blob',
};

// /**
//  * getAttachment
//  * @param {string} attId
//  * @param {string} messageId
//  * @param {string} tok
//  * @param {string} dc
//  * @return {JSON} attachment response json
//  */
export const getAttachment = stats.withMetric(
    {
      metric: stats.METRIC.GET_ATTACH,
      description: 'fetchAtt',
    },
    async function getAttachment({attId, dc, messageId, tok}) {
      const queryParams = new URLSearchParams({
        attId: attId,
        id: messageId,
        tok,
        dc,
      });
      const URL = ATT_BASE_URL + '?' + queryParams;
      const response = await fetch(URL, GET_ATT_OPTIONS);
      if (response.redirected) {
        console.log('Redirected to', response.url);
      }
      if (!response.ok) {
        console.log('Failed to download attachment');
        throw new Error(
            `Att fetch failed: ${response.status} ${response.statusText}`,
        );
      }
      console.log(response);
      return response;
    },
);

/**
 * streamAttachment
 * @param {Object} attParams
 * @param {string} attParams.attId
 * @param {string} attParams.dc
 * @param {string} attParams.messageId
 * @param {string} attParams.tok
 * @param {function} onProgress callback for download progress changes
 * @param {function} onPauseChange callback for download pause/resume updates
 * @return {StreamController} StreamController instance
 */
export const streamAttachment = (
    {attId, dc, messageId, tok}, onProgress, onPauseChange) => {
  const abortController = new AbortController();
  const queryParams = new URLSearchParams({
    attId: attId,
    id: messageId,
    tok,
    dc,
  });
  const URL = ATT_BASE_URL + '?' + queryParams;
  const options = {...GET_ATT_OPTIONS, signal: abortController.signal};
  const pendingResponse = stats.withMetric(
      {
        metric: stats.METRIC.GET_ATTACH,
        description: 'fetchAtt',
      },
      async () => await fetch(URL, options).then((response) => {
        if (response.redirected) {
          console.log('Redirected to', response.url);
        }
        if (!response.ok) {
          console.log('Failed to download attachment');
          throw new Error(
              `Att fetch failed: ${response.status} ${response.statusText}`,
          );
        }
        return response;
      }),
  )();

  // Return a controller to manage stream state
  return new StreamController(
      pendingResponse,
      abortController,
      onProgress,
      onPauseChange,
  );
};

/**
 * Class to read a pending fetch Response object
 *  with progress and pause controls
 */
export class StreamController {
  onProgress = null;
  onPauseChange = null;
  response = null;
  pendingResponse = null;
  abortController = null;

  status = {
    responsePending: false,
    done: false,
    progress: 0,
    paused: false,
    cancelled: false,
    streamSupported: false,
    pausable: false,
    abortable: false,
  };

  /**
   *
   * @param {Promise<Response>} pendingResponse Promise resolving
   *  to a fetch Response
   * @param {AbortController} controller AbortController for aborting the fetch
   * @param {?function} onProgress callback for progress changes
   * @param {?function} onPauseChange callback for pause / play state changes
   */
  constructor(pendingResponse, controller, onProgress, onPauseChange) {
    this.status.responsePending = true;
    this.pendingResponse = pendingResponse.then((resp) => {
      this.status.responsePending = false;
      this.status.pausable = true;
      this.status.streamSupported = resp.body ? true : false;
      return resp;
    });
    this.abortController = controller;
    if (controller) {
      this.status.abortable = true;
    }
    this.onProgress = onProgress;
    this.onPauseChange = onPauseChange;
  }

  /**
   * Cancels the read
   */
  abort() {
    if (this.status.abortable) {
      this.status.cancelled = true;
      this.abortController.abort();
    } else {
      console.warn('download is not cancellable');
    }
  }

  /**
   * Pauses stream reading
   */
  pause() {
    if (this.status.pausable) {
      this.status.paused = true;
      this.onPauseChange && this.onPauseChange(this.status.paused);
    } else {
      console.warn('download is not pausable');
    }
  }

  /**
   * Resumes stream reading
   */
  resume() {
    this.status.paused = false;
    this.onPauseChange && this.onPauseChange(this.status.paused);
  }

  /**
   * Reads response data using ReadableStream if available;
   * otherwise falls back to response.json()
   * @return {Object} parsed json object
   */
  async read() {
    this.response = await this.pendingResponse;
    // fetch response.body is falsy when
    // browser does not have ReadableStream support
    this.status.streamSupported = this.response.body ? true : false;
    if (this.status.streamSupported) {
      const reader = this.response.body.getReader();
      const chunks = [];
      // read() returns a promise that resolves
      // when a value has been received
      while (!this.status.done) {
        if (this.status.cancelled) {
          throw new Error('request cancelled');
        } else if (this.status.paused) {
          // do nothing
          await new Promise((resolve) => setTimeout(resolve, 300));
        } else {
          const readChunk = await reader.read();
          this.status.done = readChunk.done;
          if (!this.status.done) {
            chunks.push(readChunk.value);
            this.status.progress += readChunk.value.byteLength;
            this.onProgress && this.onProgress(this.status.progress);
          }
        }
      }
      const chunksAll = new Uint8Array(this.status.progress);
      let position = 0;
      for (const chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
      }
      const result = new TextDecoder('utf-8').decode(chunksAll);
      this.status.data = JSON.parse(result);
      return this.status.data;
    }

    console.log('stream unsupported in browser');
    // ReadableStream is unavailable in browser;
    // read json without progress
    return this.response.json();
  }
}
